import { useEffect, useMemo, useState } from "react";
import { api } from "../api";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Card } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Switch } from "../components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Plus, Trash2, Building2 } from "lucide-react";

const rupee = (v) => "₹ " + Number(v||0).toLocaleString("en-IN", { maximumFractionDigits: 2 });
const blankItem = { item: "", qty: 1, rate: 0, tax_pct: 18, product_id: "" };

function Vendors({ onChanged }) {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", phone: "", email: "", address: "", gstin: "", state_code: "" });

  const load = async () => { const { data } = await api.get("/vendors"); setItems(data); onChanged?.(data); };
  useEffect(() => { load(); }, []);

  const save = async () => {
    try { if (editing) await api.put(`/vendors/${editing.id}`, form); else await api.post("/vendors", form);
      toast.success("Saved"); setOpen(false); setEditing(null); setForm({ name:"", phone:"", email:"", address:"", gstin:"", state_code:"" }); load();
    } catch { toast.error("Failed"); }
  };
  const del = async (id) => { if(!window.confirm("Delete vendor?")) return; try { await api.delete(`/vendors/${id}`); load(); } catch { toast.error("Admin only"); } };

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Dialog open={open} onOpenChange={(v)=>{setOpen(v); if(!v){setForm({ name:"", phone:"", email:"", address:"", gstin:"", state_code:"" }); setEditing(null);}}}>
          <DialogTrigger asChild><Button className="bg-primary hover:bg-[#984D38]" data-testid="add-vendor-btn"><Plus size={14} className="mr-2"/>Add Vendor</Button></DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle className="font-serif-elegant text-2xl">{editing?"Edit":"New"} Vendor</DialogTitle></DialogHeader>
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2"><Label>Name</Label><Input value={form.name} onChange={e=>setForm({...form,name:e.target.value})} data-testid="vendor-name"/></div>
              <div><Label>Phone</Label><Input value={form.phone} onChange={e=>setForm({...form,phone:e.target.value})}/></div>
              <div><Label>Email</Label><Input value={form.email} onChange={e=>setForm({...form,email:e.target.value})}/></div>
              <div><Label>GSTIN</Label><Input value={form.gstin} onChange={e=>setForm({...form,gstin:e.target.value})}/></div>
              <div><Label>State code</Label><Input value={form.state_code} onChange={e=>setForm({...form,state_code:e.target.value})}/></div>
              <div className="col-span-2"><Label>Address</Label><Textarea rows={2} value={form.address} onChange={e=>setForm({...form,address:e.target.value})}/></div>
            </div>
            <DialogFooter><Button onClick={save} className="bg-primary hover:bg-[#984D38]" data-testid="vendor-save">Save</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
      <Card className="border-border bg-white overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-left text-xs uppercase text-muted-foreground bg-secondary/50">
            <tr><th className="py-3 px-4">Name</th><th className="py-3 px-4">Contact</th><th className="py-3 px-4">GSTIN</th><th className="py-3 px-4">Address</th><th></th></tr>
          </thead>
          <tbody>
            {items.length===0 && <tr><td colSpan={5} className="py-12 text-center text-muted-foreground"><Building2 className="mx-auto mb-2 opacity-40" size={28}/>No vendors yet.</td></tr>}
            {items.map(v => (
              <tr key={v.id} className="border-t hairline hover:bg-accent/30">
                <td className="py-3 px-4 font-medium">{v.name}</td>
                <td className="py-3 px-4 text-xs text-muted-foreground">{v.phone}<br/>{v.email}</td>
                <td className="py-3 px-4">{v.gstin || "-"}</td>
                <td className="py-3 px-4 max-w-xs truncate text-muted-foreground">{v.address}</td>
                <td className="py-3 px-4 text-right"><Button size="sm" variant="outline" onClick={()=>{ setEditing(v); setForm(v); setOpen(true); }}>Edit</Button><Button size="icon" variant="ghost" onClick={()=>del(v.id)}><Trash2 size={14} className="text-destructive"/></Button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
    </div>
  );
}

function PurchaseList() {
  const [items, setItems] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [products, setProducts] = useState([]);
  const [open, setOpen] = useState(false);
  const [vendorId, setVendorId] = useState("");
  const [billNumber, setBillNumber] = useState("");
  const [date, setDate] = useState("");
  const [notes, setNotes] = useState("");
  const [isIgst, setIsIgst] = useState(false);
  const [rows, setRows] = useState([{ ...blankItem }]);

  const load = async () => { const { data } = await api.get("/purchases"); setItems(data); };
  useEffect(() => {
    load();
    api.get("/vendors").then(r=>setVendors(r.data));
    api.get("/products").then(r=>setProducts(r.data));
  }, []);

  const totals = useMemo(() => {
    let sub=0, tax=0;
    rows.forEach(it => { const line = Number(it.qty||0)*Number(it.rate||0); sub += line; tax += line*(Number(it.tax_pct||0)/100); });
    return { sub, tax, total: sub+tax };
  }, [rows]);
  const addRow = () => setRows([...rows, { ...blankItem }]);
  const rmRow = (i) => setRows(rows.filter((_,idx)=>idx!==i));
  const upd = (i, k, v) => setRows(rows.map((it,idx)=> idx===i?{...it, [k]: k==="item"||k==="product_id"?v:Number(v||0)}:it));
  const pickProd = (i, pid) => { const p = products.find(x=>x.id===pid); if(!p) return; setRows(rows.map((it,idx)=> idx===i?{...it, product_id:pid, item:p.name, tax_pct:p.tax_pct, hsn:p.hsn||""}:it)); };
  const save = async () => {
    if (!vendorId) return toast.error("Select vendor");
    if (rows.some(r => !r.item)) return toast.error("Fill item in all rows");
    try {
      await api.post("/purchases", { vendor_id: vendorId, bill_number: billNumber, date: date||undefined, notes, is_igst: isIgst, items: rows });
      toast.success("Purchase recorded — stock updated"); setOpen(false);
      setVendorId(""); setBillNumber(""); setDate(""); setNotes(""); setIsIgst(false); setRows([{...blankItem}]); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button className="bg-primary hover:bg-[#984D38]" data-testid="new-purchase-btn"><Plus size={14} className="mr-2"/>New Purchase</Button></DialogTrigger>
          <DialogContent className="max-w-3xl">
            <DialogHeader><DialogTitle className="font-serif-elegant text-2xl">Record Purchase</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div>
                  <Label>Vendor</Label>
                  <Select value={vendorId} onValueChange={setVendorId}>
                    <SelectTrigger data-testid="purchase-vendor"><SelectValue placeholder="Select vendor"/></SelectTrigger>
                    <SelectContent>{vendors.map(v => <SelectItem key={v.id} value={v.id}>{v.name}{v.gstin ? ` · ${v.gstin}` : ""}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>Bill #</Label><Input value={billNumber} onChange={e=>setBillNumber(e.target.value)} data-testid="purchase-bill"/></div>
                <div><Label>Date</Label><Input type="date" value={date} onChange={e=>setDate(e.target.value)} data-testid="purchase-date"/></div>
              </div>
              <div className="flex items-center gap-2"><Switch id="pigst" checked={isIgst} onCheckedChange={setIsIgst}/><Label htmlFor="pigst">Inter-state (IGST)</Label></div>
              <Card className="p-3 border-border">
                <div className="grid grid-cols-12 gap-2 text-xs uppercase text-muted-foreground pb-2">
                  <div className="col-span-6">Item</div><div className="col-span-2 text-right">Qty</div><div className="col-span-2 text-right">Rate</div><div className="col-span-1 text-right">Tax%</div><div className="col-span-1"></div>
                </div>
                {rows.map((it,i)=>(
                  <div key={i} className="grid grid-cols-12 gap-2 mb-2 items-center">
                    <div className="col-span-6 flex gap-1">
                      {products.length>0 && (
                        <Select value={it.product_id||""} onValueChange={(v)=>pickProd(i,v)}>
                          <SelectTrigger className="w-9 p-0 border-0 bg-transparent"><SelectValue placeholder="📦"/></SelectTrigger>
                          <SelectContent>{products.map(p=><SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
                        </Select>
                      )}
                      <Input className="flex-1" placeholder="Item" value={it.item} onChange={e=>upd(i,"item",e.target.value)} data-testid={`p-row-item-${i}`}/>
                    </div>
                    <Input className="col-span-2 text-right" type="number" value={it.qty} onChange={e=>upd(i,"qty",e.target.value)} data-testid={`p-row-qty-${i}`}/>
                    <Input className="col-span-2 text-right" type="number" value={it.rate} onChange={e=>upd(i,"rate",e.target.value)} data-testid={`p-row-rate-${i}`}/>
                    <Input className="col-span-1 text-right" type="number" value={it.tax_pct} onChange={e=>upd(i,"tax_pct",e.target.value)}/>
                    <div className="col-span-1">{rows.length>1 && <Button size="icon" variant="ghost" onClick={()=>rmRow(i)}><Trash2 size={14} className="text-destructive"/></Button>}</div>
                  </div>
                ))}
                <Button variant="outline" size="sm" onClick={addRow}><Plus size={14} className="mr-2"/>Add row</Button>
              </Card>
              <div className="flex justify-end gap-6 text-sm">
                <div><span className="text-muted-foreground">Subtotal:</span> <b className="ml-2">{rupee(totals.sub)}</b></div>
                <div><span className="text-muted-foreground">Tax:</span> <b className="ml-2">{rupee(totals.tax)}</b></div>
                <div className="font-serif-elegant text-xl"><span className="text-muted-foreground">Total:</span> <b className="ml-2 text-primary">{rupee(totals.total)}</b></div>
              </div>
              <Textarea rows={2} value={notes} onChange={e=>setNotes(e.target.value)} placeholder="Notes"/>
            </div>
            <DialogFooter><Button onClick={save} className="bg-primary hover:bg-[#984D38]" data-testid="purchase-save">Record Purchase</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
      <Card className="border-border bg-white overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-left text-xs uppercase text-muted-foreground bg-secondary/50">
            <tr><th className="py-3 px-4">Date</th><th className="py-3 px-4">Bill</th><th className="py-3 px-4">Vendor</th><th className="py-3 px-4 text-right">Subtotal</th><th className="py-3 px-4 text-right">Tax</th><th className="py-3 px-4 text-right">Total</th></tr>
          </thead>
          <tbody>
            {items.length===0 && <tr><td colSpan={6} className="py-12 text-center text-muted-foreground">No purchases yet.</td></tr>}
            {items.map(p => (
              <tr key={p.id} className="border-t hairline">
                <td className="py-3 px-4">{p.date}</td>
                <td className="py-3 px-4">{p.bill_number || "-"}</td>
                <td className="py-3 px-4">{p.vendor?.name}</td>
                <td className="py-3 px-4 text-right">{rupee(p.subtotal)}</td>
                <td className="py-3 px-4 text-right">{rupee(p.tax_total)}</td>
                <td className="py-3 px-4 text-right font-semibold">{rupee(p.total)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
    </div>
  );
}

export default function Purchases() {
  return (
    <div className="space-y-6">
      <div>
        <p className="text-xs uppercase tracking-[0.3em] text-muted-foreground">Buying</p>
        <h1 className="font-serif-elegant text-5xl mt-1">Purchases</h1>
      </div>
      <Tabs defaultValue="purchases">
        <TabsList>
          <TabsTrigger value="purchases" data-testid="tab-purchases">Bills</TabsTrigger>
          <TabsTrigger value="vendors" data-testid="tab-vendors">Vendors</TabsTrigger>
        </TabsList>
        <TabsContent value="purchases" className="mt-4"><PurchaseList/></TabsContent>
        <TabsContent value="vendors" className="mt-4"><Vendors/></TabsContent>
      </Tabs>
    </div>
  );
}
